import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { Layout } from './components/layout/Layout';
import { ProtectedRoute } from './components/layout/ProtectedRoute';
import { MarketingLayout } from './components/layout/MarketingLayout';
import { SuperAdminLayout } from './components/layout/SuperAdminLayout';

// Lazy-loaded page components (reduces initial bundle size)
// Auth pages
const LoginPage = lazy(() => import('./pages/auth/LoginPage').then(m => ({ default: m.LoginPage })));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const ChangePasswordPage = lazy(() => import('./pages/auth/ChangePasswordPage').then(m => ({ default: m.ChangePasswordPage })));
const MFAEnrollPage = lazy(() => import('./pages/auth/MFAEnrollPage').then(m => ({ default: m.MFAEnrollPage })));
const MagicLinkCallbackPage = lazy(() => import('./pages/auth/MagicLinkCallbackPage').then(m => ({ default: m.MagicLinkCallbackPage })));

// Public pages
const PublicSharePage = lazy(() => import('./pages/public/PublicSharePage').then(m => ({ default: m.PublicSharePage })));

// Marketing pages
const HomePage = lazy(() => import('./pages/marketing/HomePage').then(m => ({ default: m.HomePage })));
const PricingPage = lazy(() => import('./pages/marketing/PricingPage').then(m => ({ default: m.PricingPage })));
const RequestAccessPage = lazy(() => import('./pages/marketing/RequestAccessPage').then(m => ({ default: m.RequestAccessPage })));
const TermsPage = lazy(() => import('./pages/marketing/TermsPage').then(m => ({ default: m.TermsPage })));
const PrivacyPage = lazy(() => import('./pages/marketing/PrivacyPage').then(m => ({ default: m.PrivacyPage })));
const FeaturesPage = lazy(() => import('./pages/marketing/FeaturesPage').then(m => ({ default: m.FeaturesPage })));
const SecurityPage = lazy(() => import('./pages/marketing/SecurityPage').then(m => ({ default: m.SecurityPage })));
const ContactPage = lazy(() => import('./pages/marketing/ContactPage').then(m => ({ default: m.ContactPage })));

// App pages
const DashboardPage = lazy(() => import('./pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const FoldersPage = lazy(() => import('./pages/FoldersPage').then(m => ({ default: m.FoldersPage })));
const ShareLinksPage = lazy(() => import('./pages/ShareLinksPage').then(m => ({ default: m.ShareLinksPage })));

// Admin pages
const UsersPage = lazy(() => import('./pages/admin/UsersPage').then(m => ({ default: m.UsersPage })));
const AuditLogsPage = lazy(() => import('./pages/admin/AuditLogsPage').then(m => ({ default: m.AuditLogsPage })));
const SettingsPage = lazy(() => import('./pages/admin/SettingsPage').then(m => ({ default: m.SettingsPage })));

// Super admin pages
const SuperDashboardPage = lazy(() => import('./pages/super/DashboardPage').then(m => ({ default: m.SuperDashboardPage })));
const TenantsPage = lazy(() => import('./pages/super/TenantsPage').then(m => ({ default: m.TenantsPage })));
const AccessRequestsPage = lazy(() => import('./pages/super/AccessRequestsPage').then(m => ({ default: m.AccessRequestsPage })));

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Loading fallback for lazy-loaded pages
function PageLoading() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <Suspense fallback={<PageLoading />}>
            <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/s/:token" element={<PublicSharePage />} />

            {/* Auth flow routes (require auth but not full onboarding) */}
            <Route path="/auth/change-password" element={<ChangePasswordPage />} />
            <Route path="/auth/mfa-enroll" element={<MFAEnrollPage />} />
            <Route path="/auth/magic-link-callback" element={<MagicLinkCallbackPage />} />

            {/* Protected routes */}
            <Route
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/folders" element={<FoldersPage />} />
              <Route path="/folders/:id" element={<FoldersPage />} />
              <Route path="/share-links" element={<ShareLinksPage />} />

              {/* Admin routes */}
              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute requireAdmin>
                    <UsersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/audit"
                element={
                  <ProtectedRoute requireAdmin>
                    <AuditLogsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/settings"
                element={
                  <ProtectedRoute requireAdmin>
                    <SettingsPage />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Super admin routes */}
            <Route
              element={
                <ProtectedRoute>
                  <SuperAdminLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/super" element={<SuperDashboardPage />} />
              <Route path="/super/tenants" element={<TenantsPage />} />
              <Route path="/super/access-requests" element={<AccessRequestsPage />} />
            </Route>

            {/* Marketing/public pages */}
            <Route element={<MarketingLayout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/features" element={<FeaturesPage />} />
              <Route path="/security" element={<SecurityPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/request-access" element={<RequestAccessPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/contact" element={<ContactPage />} />
            </Route>

            {/* Catch-all redirect */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
